import React, { useState, useEffect } from 'react';
import { MdClose, MdGroup } from 'react-icons/md';
import axios from 'axios';

const AddGroupModal = ({ isOpen, onClose, showPopup, fetchData, API_BASE_URL, userMmdaId, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    group_name: '',
    zone_id: '',
    description: '',
    status: 'active',
  });
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zonesLoading, setZonesLoading] = useState(false);

  const config = {
    headers: {
      'Authorization': 'Basic ' + btoa('Techland:Techland@24'),
      'Content-Type': 'application/json',
    },
  };

  // Fetch zones for the MMDA
  const fetchZones = async () => {
    setZonesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/zones?userRole=MMDA&mmda_id=${userMmdaId}`, config);
      setZones(response.data);
    } catch (error) {
      showPopup('Failed to load zones', 'error');
    } finally {
      setZonesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchZones();
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Add MMDA ID to the group data
      const groupData = {
        ...formData,
        mmda_id: userMmdaId,
      };

      const response = await axios.post(`${API_BASE_URL}/zonal-groups`, groupData, config);
      
      if (response.status === 201) {
        setFormData({
          group_name: '',
          zone_id: '',
          description: '',
          status: 'active',
        });
        onClose();

        // Show success modal
        if (onSuccess) {
          onSuccess('Created', 'Group', formData.group_name, 'Group has been successfully created and is now available for officer assignments.');
        } else {
          showPopup('Group created successfully!', 'success');
          fetchData();
        }
      }
    } catch (error) {
      // Show error modal
      if (onError) {
        onError('Create', 'Group', formData.group_name, error.response?.data?.error || error.message);
      } else {
        showPopup(error.response?.data?.error || 'Failed to create group', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      group_name: '',
      zone_id: '',
      description: '',
      status: 'active',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 24,
        width: '90%',
        maxWidth: 500,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 15,
          borderBottom: '2px solid #e0e0e0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MdGroup size={24} style={{ color: '#28a745' }} />
            <h2 style={{ margin: 0, color: '#28a745', fontSize: 20 }}>Add New Group</h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 5,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              marginBottom: 5,
              fontWeight: 'bold',
              color: '#333',
              fontSize: 14,
            }}>
              Group Name *
            </label>
            <input
              type="text"
              name="group_name"
              value={formData.group_name}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              placeholder="Enter group name"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              marginBottom: 5,
              fontWeight: 'bold',
              color: '#333',
              fontSize: 14,
            }}>
              Zone *
            </label>
            <select
              name="zone_id"
              value={formData.zone_id}
              onChange={handleInputChange}
              required
              disabled={zonesLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: zonesLoading ? '#f5f5f5' : 'white',
              }}
            >
              <option value="">
                {zonesLoading ? 'Loading zones...' : 'Select a zone'}
              </option>
              {zones.map(zone => (
                <option key={zone.zone_id} value={zone.zone_id}>
                  {zone.zone_name} ({zone.zone_code})
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block',
              marginBottom: 5,
              fontWeight: 'bold',
              color: '#333',
              fontSize: 14,
            }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
              placeholder="Enter group description"
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block',
              marginBottom: 5,
              fontWeight: 'bold',
              color: '#333',
              fontSize: 14,
            }}>
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: 'white',
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Regional Info */}
          <div style={{
            padding: 12,
            backgroundColor: '#e8f5e8',
            borderRadius: 6,
            marginBottom: 20,
            fontSize: 13,
            color: '#2e7d32',
          }}>
            <strong>Note:</strong> This group will be created within the selected zone in your MMDA region.
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #ccc',
                borderRadius: 4,
                backgroundColor: 'white',
                color: '#666',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || zonesLoading || !formData.zone_id}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: 4,
                backgroundColor: (loading || zonesLoading || !formData.zone_id) ? '#ccc' : '#28a745',
                color: 'white',
                cursor: (loading || zonesLoading || !formData.zone_id) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {loading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGroupModal;
